import React from 'react';

import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import { CheckOutlined, EyeOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { SortDirection, TenantProductName, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { GetInventoryDashboardStatusItemsQueryParams } from '@recurrency/core-api-schema/dist/inventory/getInventoryDashboard';
import {
  InventoryDashboardStatus,
  InventoryDashboardStatusRecordDTO,
} from '@recurrency/core-api-schema/dist/inventory/inventoryDashboardResultDTO';
import { Divider, Radio } from 'antd';
import { SortOrder } from 'antd/lib/table/interface';
import { ZipCelXSheet } from 'zipcelx';

import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { FlexSpace } from 'components/FlexSpace';
import { CenteredError } from 'components/Loaders';
import { PageHeader } from 'components/PageHeader';
import { RadioGroup } from 'components/Radio/RadioGroup';
import { ColumnChooserSection } from 'components/recipes/ColumnChooserSection';
import { DownloadButton, DownloadXSheetColumn, recordsToXSheet } from 'components/recipes/DownloadButton';
import { NetStockPopover, NetStockRecord } from 'components/recipes/equation/NetStockPopover';
import { ItemLocationNotesViewModal } from 'components/recipes/ItemLocationNotesViewModal';
import { flattenExportColumns } from 'components/recipes/SearchFrame/utils';
import { BadgeStatus, StatusBadge } from 'components/recipes/StatusBadge';
import { DEFAULT_PAGE_SIZE, Table } from 'components/Table';
import { Tooltip } from 'components/Tooltip';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';
import { EllipsisTruncatedCell } from 'components/Typography/EllipsisTruncatedLabel';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch, fetchAllRecordsInBatches } from 'utils/api';
import { arrUnique } from 'utils/array';
import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { formatDate, formatNumber, formatUSD } from 'utils/formatting';
import { LocalStorageKey, useLocalStorage } from 'utils/localStorage';
import { shouldShowProduct } from 'utils/roleAndTenant';
import { routes, useHashState } from 'utils/routes';
import { PersistedColumn, ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { useUserViewSettingsState } from 'utils/tableAndSidePaneSettings/useUserViewSettingsState';
import { asKeyOf, ExportColumn } from 'utils/tables';
import { isTenantErpTypeP21, isTenantErpTypeNetSuite } from 'utils/tenants';
import { getTenantSetting } from 'utils/tenantSettings';

import { InventoryDashboardHashStateFilters, InventoryDashboardStatusHashState } from 'types/hash-state';

import { ReplenishmentMethod } from '../../utils';
import { DashboardUnitSelect } from '../shared/DashboardUnitSelect';
import { InventoryDashboardFilters } from '../shared/InventoryDashboardFilters';
import { RecommendedMaxToggle } from '../shared/RecommendedMaxToggle';
import { ResultsCounter } from '../shared/ResultsCounter';
import { KPIBar } from './KPIBar';

export const CurrentInventoryExplorerPage = () => {
  const { activeTenant } = useGlobalApp();
  const history = useHistory();
  const [hashState, updateHashState] = useHashState<InventoryDashboardStatusHashState>();
  // Show min/max toggle if forecasting is enabled and running in the backend to generate min/maxes
  const shouldShowRecommendedToggle = shouldShowProduct(activeTenant, TenantProductName.DfpaDemandPlanning, true);
  const useRecommendedMax = Boolean(shouldShowRecommendedToggle && (hashState?.useRecommendedMax ?? false));
  const [localStorageRecommendedMaxEnabled, setLocalStorageRecommendedMaxEnabled] = useLocalStorage(
    LocalStorageKey.Demand_Planning_Recommended_Max_Toggle_Enabled,
    getTenantSetting(TenantSettingKey.FeaturePlanningMaxToggleDefault) ?? false,
  );

  const defaultHashState: InventoryDashboardStatusHashState = {
    page: 1,
    sortBy: [['onHandValue', SortDirection.Desc]],
    displayUnit: 'value',
  };

  if (!hashState || !hashState.displayUnit) {
    requestAnimationFrame(async () => {
      updateHashState(defaultHashState);
    });
  }
  // Set default for max toggle to the tenant setting
  if (!hashState || hashState.useRecommendedMax === undefined) {
    updateHashState({
      useRecommendedMax: localStorageRecommendedMaxEnabled,
    });
  }

  const viewValue = () => hashState.displayUnit === 'value';

  const recommendedVariants = {
    status: `recommendedStatus`,
    maxStock: `recommendedMaxStock`,
    normalQty: `recommendedNormalQty`,
    normalValue: `recommendedNormalValue`,
    overstockQty: `recommendedOverstockQty`,
    overstockValue: `recommendedOverstockValue`,
  };

  // Retrieve any recommended variants of the requested fields
  const appendRecommendedVariants = (requestedFields?: string[]) => {
    if (!requestedFields) {
      return requestedFields;
    }
    const newRecommendedFields = requestedFields
      .filter((field) => field in recommendedVariants)
      .map((field) => recommendedVariants[field as keyof typeof recommendedVariants]);
    return [...requestedFields, ...newRecommendedFields];
  };

  const requiredFields = {
    status: ['onHandQty', 'allocatedQty', 'minStock', 'replenishmentMethod', 'maxStock'],
    recommendedStatus: ['onHandQty', 'allocatedQty', 'minStock', 'replenishmentMethod', 'recommendedMaxStock'],
    // always show both onhand values
    onHandValue: ['onHandQty'],
    onHandQty: ['onHandValue'],
    // Net Stock popover needs all qty fields + uom
    netStock: [
      'onHandQty',
      'allocatedQty',
      'onPurchaseOrderQty',
      'onUnapprovedPurchaseOrderQty',
      'inTransitQty',
      'inProcessQty',
      'backorderedQty',
      'reservedDueInQty',
      'inProductionQty',
      'forProductionQty',
      'quarantinedQty',
      'frozenQty',
      'nonPickableQty',
      'onReleaseScheduleQty',
      'onPendingBackorderQty',
      'onBlanketPurchaseOrderQty',
      'unitOfMeasure',
    ],
  };

  // Certain fields require others to be present, so this includes those
  const appendRequiredFields = (requestedFields?: string[]) => {
    if (!requestedFields) {
      return requestedFields;
    }
    requestedFields.forEach((field) => {
      if (field in requiredFields) {
        requiredFields[field as keyof typeof requiredFields].forEach((requiredField: string) => {
          if (!requestedFields.includes(requiredField)) {
            requestedFields.push(requiredField);
          }
        });
      }
    });
    return requestedFields;
  };

  const sorterDirection = {
    ascend: SortDirection.Asc,
    descend: SortDirection.Desc,
  };

  const onHashStateFilterChange = (filters: InventoryDashboardHashStateFilters) => {
    updateHashState({ filters, page: 1 });
  };

  const toggleMax = (value: boolean) => {
    updateHashState({ useRecommendedMax: value });
    setLocalStorageRecommendedMaxEnabled(value);
  };

  const handleItemLocationNotesModal = (record: InventoryDashboardStatusRecordDTO) => async () => {
    await showAsyncModal(ItemLocationNotesViewModal, {
      title: `${record.itemName} at ${record.locationId}: ${record.locationName}`,
      notes: record.itemLocationNotes || '',
    });
  };

  const orZero = (value?: number) => value ?? 0;

  // Adjust max to be OP + OQ for OP/OQ items
  const getMaxQuantity = (record: InventoryDashboardStatusRecordDTO) => {
    const baseMax = (useRecommendedMax ? record.recommendedMaxStock : record.maxStock) ?? 0;
    const maxAdjustment = (record.replenishmentMethod === ReplenishmentMethod.OPOQ ? record.minStock : 0) ?? 0;
    return formatNumber(baseMax + maxAdjustment);
  };

  const statusMessage = (record: InventoryDashboardStatusRecordDTO) =>
    (useRecommendedMax ? record.recommendedStatus : record.status) === 'Normal' ? (
      <div>
        <div>
          <strong>Normal</strong>
        </div>
        Inventory volume is normal
      </div>
    ) : (useRecommendedMax ? record.recommendedStatus : record.status) === 'Overstock' ? (
      <div>
        <div>
          <strong>Overstock</strong>
        </div>
        The quantity on hand of {formatNumber(orZero(record.onHandQty))}
        <br />
        exceeds the{' '}
        {record.replenishmentMethod === ReplenishmentMethod.OPOQ
          ? 'Order Point + Order Quantity'
          : 'current max'} of {getMaxQuantity(record)}
      </div>
    ) : (
      <div>
        <div>
          <strong>Dead Stock</strong>
        </div>
        Last Invoice: {formatDate(record.lastInvoiceDate)}
        <br />
        Last Purchased: {formatDate(record.lastPurchaseOrderDate)}
        <br />
        Last Sold: {formatDate(record.lastSalesOrderDate)}
        <br />
        Last outgoing transfer: {formatDate(record.lastTransferDate)}
      </div>
    );

  const formatValueOrVolume = (value?: number) =>
    hashState.displayUnit === 'value' ? formatUSD(value) : formatNumber(value);

  const dataIndex = (field: string) => (viewValue() ? `${field}Value` : `${field}Qty`);

  const columnSortDirection = (columnName: string, useDefault = false): 'ascend' | 'descend' | undefined => {
    if (useDefault && (!hashState.sortBy || hashState.sortBy.length < 1)) return 'ascend';

    const inSortBy = (hashState.sortBy ?? []).find(([sortField]) => sortField === columnName);
    return !inSortBy ? undefined : inSortBy[1] === SortDirection.Asc ? 'ascend' : 'descend';
  };

  function numericColumnSorter<RecordT>(field: string, a: RecordT, b: RecordT, sortDirection?: SortOrder) {
    if (!sortDirection) return 0;
    const key = dataIndex(field) as keyof RecordT;
    return sortDirection === 'ascend' ? Number(b[key]) - Number(a[key]) : Number(a[key]) - Number(b[key]);
  }

  const columns: (PersistedColumn<InventoryDashboardStatusRecordDTO> | null)[] = [
    {
      title: 'Item',
      settingKey: 'itemCode',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('itemCode'),
      fixed: 'left',
      render: (_: string, record) => (
        <div>
          <Link to={routes.purchasing.itemDetails(record.itemCode)}>{record.itemCode}</Link>
          <div>{record.itemName}</div>
        </div>
      ),
      exportValue: {
        'Item ID': asKeyOf<InventoryDashboardStatusRecordDTO>('itemCode'),
        'Item Name': asKeyOf<InventoryDashboardStatusRecordDTO>('itemName'),
      },
      exportType: 'string',
      required: true,
    },
    {
      title: 'Location',
      settingKey: 'locationId',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('locationId'),
      render: (_: string, record) => (
        <div>
          <div>{record.locationId}</div>
          <div>{record.locationName}</div>
        </div>
      ),
      exportValue: {
        'Location ID': asKeyOf<InventoryDashboardStatusRecordDTO>('locationId'),
        'Location Name': asKeyOf<InventoryDashboardStatusRecordDTO>('locationName'),
      },
      exportType: 'string',
      required: true,
    },
    {
      title: 'Primary Supplier',
      settingKey: 'supplierId',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('supplierId'),
      render: (_: string, record) => (
        <div>
          <div>{record.supplierId}</div>
          <div>{record.supplierName}</div>
        </div>
      ),
      exportValue: {
        'Supplier ID': asKeyOf<InventoryDashboardStatusRecordDTO>('supplierId'),
        'Supplier Name': asKeyOf<InventoryDashboardStatusRecordDTO>('supplierName'),
      },
      exportType: 'string',
    },
    {
      title: 'Supplier Part ID',
      settingKey: 'supplierPartId',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('supplierPartId'),
      align: 'left',
      render: (value: string | undefined) => value || null,
      optional: true,
      exportType: 'string',
    },
    {
      title: 'Status',
      settingKey: 'status',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('status'),
      render: (_, record) => (
        <Tooltip title={<div>{statusMessage(record)}</div>}>
          <StatusBadge
            status={BadgeStatus[useRecommendedMax ? record.recommendedStatus : record.status]}
            className={css`
              cursor: help;
            `}
          />
        </Tooltip>
      ),
      exportType: 'string',
    },
    isTenantErpTypeP21(activeTenant.erpType)
      ? {
          title: 'ABC Class',
          settingKey: 'purchaseClass',
          dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('purchaseClass'),
          exportType: 'string',
        }
      : null,
    {
      title: 'Stockable',
      settingKey: 'stockable',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('stockable'),
      align: 'center',
      render: (value) => (value === true ? <CheckOutlined /> : null),
      exportType: 'string',
    },
    {
      title: 'On Hand',
      settingKey: 'onHand',
      children: [
        {
          title: 'Value',
          dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('onHandValue'),
          sorter: true,
          sortOrder: columnSortDirection('onHandValue'),
          align: 'right' as const,
          render: (value: number | undefined) => formatUSD(value),
        },
        {
          title: 'Quantity',
          dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('onHandQty'),
          sorter: true,
          sortOrder: columnSortDirection('onHandQty'),
          align: 'right' as const,
          render: (value: number | undefined) => formatNumber(value),
        },
      ],
      exportValue: {
        'On Hand Value': asKeyOf<InventoryDashboardStatusRecordDTO>('onHandValue'),
        'On Hand Quantity': asKeyOf<InventoryDashboardStatusRecordDTO>('onHandQty'),
      },
      exportType: 'number',
    },
    {
      title: 'Net Stock',
      settingKey: 'netStock',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('netStock'),
      sorter: true,
      sortOrder: columnSortDirection('netStock'),
      align: 'right',
      render: (value: number | undefined, record) => (
        <>
          {formatNumber(value)} {record.unitOfMeasure}
          <NetStockPopover
            record={
              {
                netStock: record.netStock ?? 0,
                aggregatedNetStock: record.netStock ?? 0,
                qtyOnHand: record.onHandQty ?? 0,
                qtyAllocated: record.allocatedQty ?? 0,
                qtyOnPurchaseOrder: record.onPurchaseOrderQty ?? 0,
                qtyOnUnapprovedPurchaseOrder: record.onUnapprovedPurchaseOrderQty ?? 0,
                qtyInTransit: record.inTransitQty ?? 0,
                qtyInProcess: record.inProcessQty ?? 0,
                qtyBackordered: record.backorderedQty ?? 0,
                qtyReservedDueIn: record.reservedDueInQty ?? 0,
                qtyInProduction: record.inProductionQty ?? 0,
                qtyForProduction: record.forProductionQty ?? 0,
                qtyQuarantined: record.quarantinedQty ?? 0,
                qtyFrozen: record.frozenQty ?? 0,
                qtyNonPickable: record.nonPickableQty ?? 0,
                qtyOnReleaseSchedule: record.onReleaseScheduleQty ?? 0,
                qtyOnPendingBackorder: record.onPendingBackorderQty ?? 0,
                qtyOnBlanketPurchaseOrder: record.onBlanketPurchaseOrderQty ?? 0,
                unitOfMeasure: record.unitOfMeasure ?? '',
              } as NetStockRecord
            }
          />
        </>
      ),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Allocated',
      settingKey: 'allocated',
      dataIndex: dataIndex('allocated'),
      sorter: (a, b, sortDirection) =>
        numericColumnSorter<InventoryDashboardStatusRecordDTO>('allocated', a, b, sortDirection),
      sortOrder: columnSortDirection(dataIndex('allocated')),
      align: 'right',
      render: (value) => formatValueOrVolume(value),
      exportType: 'number',
    },
    {
      title: 'On Order',
      settingKey: 'onOrder',
      dataIndex: dataIndex('onPurchaseOrder'),
      sorter: (a, b, sortDirection) =>
        numericColumnSorter<InventoryDashboardStatusRecordDTO>('onPurchaseOrder', a, b, sortDirection),
      sortOrder: columnSortDirection(dataIndex('onPurchaseOrder')),
      align: 'right',
      render: (value) => formatValueOrVolume(value),
      exportType: 'number',
    },
    {
      title: 'Backordered',
      settingKey: 'backordered',
      dataIndex: dataIndex('backordered'),
      sorter: (a, b, sortDirection) =>
        numericColumnSorter<InventoryDashboardStatusRecordDTO>('backordered', a, b, sortDirection),
      sortOrder: columnSortDirection(dataIndex('backordered')),
      align: 'right',
      render: (value) => formatValueOrVolume(value),
      exportType: 'number',
    },
    {
      title: 'Last Purchase Order',
      settingKey: 'lastPurchaseOrderDate',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('lastPurchaseOrderDate'),
      sorter: true,
      sortOrder: columnSortDirection('lastPurchaseOrderDate'),
      align: 'right',
      render: (value) => formatDate(value),
      exportValue: (record: InventoryDashboardStatusRecordDTO) => formatDate(record.lastPurchaseOrderDate),
      exportType: 'string',
    },
    {
      title: 'Last Sales Order',
      settingKey: 'lastSalesOrderDate',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('lastSalesOrderDate'),
      sorter: true,
      sortOrder: columnSortDirection('lastSalesOrderDate'),
      align: 'right',
      render: (value) => formatDate(value),
      exportValue: (record: InventoryDashboardStatusRecordDTO) => formatDate(record.lastSalesOrderDate),
      exportType: 'string',
    },
    {
      title: 'Last Invoice',
      settingKey: 'lastInvoiceDate',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('lastInvoiceDate'),
      sorter: true,
      sortOrder: columnSortDirection('lastInvoiceDate'),
      align: 'right',
      render: (value) => formatDate(value),
      exportValue: (record: InventoryDashboardStatusRecordDTO) => formatDate(record.lastInvoiceDate),
      exportType: 'string',
    },
    {
      title: 'Last Outgoing Transfer',
      settingKey: 'lastTransferDate',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('lastTransferDate'),
      sorter: true,
      sortOrder: columnSortDirection('lastTransferDate'),
      align: 'right',
      render: (value) => formatDate(value),
      exportValue: (record: InventoryDashboardStatusRecordDTO) => formatDate(record.lastTransferDate),
      exportType: 'string',
    },
    {
      title: 'Unique Customers (3M)',
      settingKey: 'uniqueCustomers3M',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('uniqueCustomers3M'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Unique Customers (6M)',
      settingKey: 'uniqueCustomers6M',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('uniqueCustomers6M'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Unique Customers (12M)',
      settingKey: 'uniqueCustomers12M',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('uniqueCustomers12M'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Unique Hits (3M)',
      settingKey: 'uniqueOrders3M',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('uniqueOrders3M'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Unique Hits (6M)',
      settingKey: 'uniqueOrders6M',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('uniqueOrders6M'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Unique Hits (12M)',
      settingKey: 'uniqueOrders12M',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('uniqueOrders12M'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Minimum',
      settingKey: 'minStock',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('minStock'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Maximum',
      settingKey: 'maxStock',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('maxStock'),
      align: 'right',
      render: (value) => formatNumber(value || 0, 0),
      optional: true,
      exportType: 'number',
    },
    {
      title: 'Recurrency Notes',
      settingKey: 'itemLocationNotes',
      dataIndex: asKeyOf<InventoryDashboardStatusRecordDTO>('itemLocationNotes'),
      align: 'left',
      render: (value, record) =>
        value && (
          <div
            className={css`
              display: flex;
              align-items: center;
              gap: 2px;
              justify-content: space-between;
            `}
          >
            <EllipsisTruncatedCell label={value} maxChars={20} />
            <ActionButton onClick={handleItemLocationNotesModal(record)} label={<EyeOutlined />} />
          </div>
        ),
      exportValue: (record: InventoryDashboardStatusRecordDTO) => record.itemLocationNotes || '',
      optional: true,
      exportType: 'string',
    },
  ];
  const tableColumns = columns.filter(truthy);

  const [visibleColumnKeys, setVisibleColumnKeys] = useUserViewSettingsState(
    ViewSettingKey.InventoryStatusExplorerTable,
    tableColumns.filter((column) => !column.optional),
  );

  // always query item, location, supplier fields as they are shown as id/name in same row
  const visibleDataIndexes = visibleColumnKeys
    .flatMap((key) => {
      const col = columns.find((column) => column && column.settingKey === key);
      if (!col) return [];
      const { children } = col as { children?: { dataIndex?: string }[] };
      if (children?.length) {
        return children.map((c) => c.dataIndex).filter(truthy);
      }
      return col.dataIndex ? [col.dataIndex as string] : [];
    })
    .filter(truthy);
  const apiFields =
    visibleColumnKeys && visibleColumnKeys.length > 0
      ? arrUnique([
          ...visibleDataIndexes,
          'itemCode',
          'itemName',
          'locationId',
          'locationName',
          'supplierId',
          'supplierName',
        ])
      : undefined;

  const pageSize = DEFAULT_PAGE_SIZE;
  const apiQueryParams: GetInventoryDashboardStatusItemsQueryParams = {
    filters: {
      status: useRecommendedMax ? undefined : (hashState.filters?.status as InventoryDashboardStatus[]),
      recommendedStatus: useRecommendedMax ? (hashState.filters?.status as InventoryDashboardStatus[]) : undefined,
      locationIds: hashState.filters?.locationIds,
      itemIds: hashState.filters?.itemIds,
      itemGroupIds: hashState.filters?.itemGroupIds,
      supplierIds: hashState.filters?.supplierIds,
      buyerIds: hashState.filters?.buyerIds,
      purchaseClasses: hashState.filters?.purchaseClasses,
      stockable: hashState.filters?.stockable?.length === 1 ? hashState.filters.stockable[0] === 'true' : undefined,
      backordered: hashState.filters?.backordered ? true : undefined,
      ruleIds: hashState.filters?.ruleIds,
    },
    sortBy: hashState.sortBy ?? defaultHashState.sortBy,
    fields: appendRequiredFields(appendRecommendedVariants(apiFields)),
    limit: pageSize,
    offset: ((hashState.page ?? 1) - 1) * pageSize,
  };

  // TABLE DATA
  const {
    isLoading: tableDataIsLoading,
    data: tableData,
    error: tableDataError,
  } = useCoreApi(schemas.inventory.getInventoryDashboardStatusItems, {
    queryParams: apiQueryParams,
  });

  // RESULTS COUNT
  const { isLoading: totalCountIsLoading, data: totalCountData } = useCoreApi(
    schemas.inventory.getInventoryDashboardStatusTotalCount,
    {
      queryParams: {
        filters: apiQueryParams.filters,
      },
    },
  );

  const visibleColumns = visibleColumnKeys
    .map((key) => tableColumns.find((column) => column.settingKey === key))
    .filter(truthy);

  return (
    <Container>
      <PageHeader
        title={
          <InfoTooltip
            title="Explore the status of your current inventory, filtered to the specific items and locations you want"
            placement="bottom"
          >
            Inventory Status Explorer
          </InfoTooltip>
        }
        entity={{ kind: 'Dashboard' }}
        onBack={() =>
          history.push(
            routes.demandPlanning.dashboard({
              filters: hashState.filters,
              displayUnit: hashState.displayUnit,
            }),
          )
        }
        headerActions={
          <DownloadButton
            permission={TenantUserPermission.AccessPlanningDashboardDownload}
            recordType="Inventory Status"
            getDownloadData={() => getDownloadData(apiQueryParams, visibleColumns, totalCountData?.totalCount || 0)}
          />
        }
      />
      <div>
        <div
          className={css`
            width: 100%;
            display: flex;
            flex-direction: row;
            gap: 16px;
            margin-bottom: 16px;
          `}
        >
          <FlexSpace gap={8}>
            <div>Measured in</div>
            <DashboardUnitSelect
              state={hashState.displayUnit}
              onChange={(value) => updateHashState({ displayUnit: value })}
            />
          </FlexSpace>
          <RadioGroup
            value={hashState.filters?.backordered ? 'backordered' : 'all'}
            onChange={(ev) => {
              onHashStateFilterChange({
                ...(hashState.filters ?? {}),
                backordered: ev.target.value === 'backordered' ? true : undefined,
              });
            }}
          >
            <Radio.Button value="backordered">Show Backorders</Radio.Button>
            <Radio.Button value="all">Show All</Radio.Button>
          </RadioGroup>
          <FlexSpace gap={8}>
            <RecommendedMaxToggle isChecked={useRecommendedMax} onToggle={toggleMax} />
          </FlexSpace>
        </div>
        <div
          className={css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
          `}
        >
          <InventoryDashboardFilters
            label={
              <InfoTooltip
                useInfoIcon
                title="Inventory Summary values are computed from the rows in view. Totals will change as filters include or exclude different inventory."
              >
                Filter
              </InfoTooltip>
            }
            filterState={hashState.filters}
            onFiltersChange={onHashStateFilterChange}
            showItemFilter
            showLocationFilter
            showStatusFilter
            showStockableFilter
            showRulesFilter
            showBuyerFilter={getTenantSetting(TenantSettingKey.UiShowBuyerFilter)}
            showPurchaseClassFilter={!isTenantErpTypeNetSuite(activeTenant.erpType)}
            showProductGroupFilter={!isTenantErpTypeNetSuite(activeTenant.erpType)}
          />
          <ResultsCounter isLoading={totalCountIsLoading} count={totalCountData?.totalCount} />
        </div>
        <Divider
          className={css`
            margin: 0;
            margin-bottom: 20px;
          `}
        />
        <KPIBar hashState={hashState} apiQueryParams={apiQueryParams} showRecommendedMaxVariant={useRecommendedMax} />
        <div>
          <ColumnChooserSection
            tableKey={ViewSettingKey.InventoryStatusExplorerTable}
            columns={tableColumns}
            visibleColumnKeys={visibleColumnKeys}
            setVisibleColumnKeys={setVisibleColumnKeys}
          />
          {tableDataError ? (
            <CenteredError error={tableDataError} />
          ) : (
            <Table
              stickyHeader
              scroll={{ x: true }}
              columns={visibleColumns}
              data={tableData?.items || []}
              isLoading={tableDataIsLoading}
              size="small"
              verticalAlign="center"
              onChange={(_pagination, _filters, sorter, { action }) => {
                if (action === 'sort') {
                  const sortArray = Array.isArray(sorter) ? sorter : [sorter];
                  const sortBy = sortArray
                    .filter((sortOpt) => sortOpt.order !== undefined)
                    .map((sortOpt): [string, SortDirection] => [
                      sortOpt.field as string,
                      sorterDirection[sortOpt.order ?? 'ascend'],
                    ]);
                  updateHashState({
                    sortBy,
                    page: 1,
                  });
                }
              }}
              pagination={
                !tableDataIsLoading &&
                (totalCountData?.totalCount ?? 0) > pageSize && {
                  onChange: (page) => updateHashState({ page }),
                  pageSize,
                  simple: true,
                  current: hashState.page,
                  total: totalCountData?.totalCount,
                }
              }
            />
          )}
        </div>
      </div>
    </Container>
  );
};

async function getDownloadData(
  apiQueryParams: GetInventoryDashboardStatusItemsQueryParams,
  columns: ExportColumn<InventoryDashboardStatusRecordDTO>[],
  // this is passed separately because CurrentInventoryExplorer makes a separate call to fetch totalCount
  totalCount: number,
): Promise<ZipCelXSheet> {
  const records = await fetchAllRecordsInBatches(async (offset, limit) => {
    const response = await coreApiFetch(schemas.inventory.getInventoryDashboardStatusItems, {
      queryParams: {
        ...apiQueryParams,
        offset,
        limit,
      },
    });
    // add totalCount from separate api call into response for fetchAllRecordsInBatches to correctly paginate
    response.data.totalCount = totalCount;
    return response;
  });

  const exportColumns: Array<DownloadXSheetColumn<InventoryDashboardStatusRecordDTO>> = flattenExportColumns(
    columns,
  ).map((column) => ({
    ...column,
    // set the type and value function for each column
    type: column.exportType ? column.exportType : 'string',
    value: (record: InventoryDashboardStatusRecordDTO) => {
      const value =
        typeof column.exportValue === 'function'
          ? column.exportValue(record)
          : record[column.dataIndex as keyof InventoryDashboardStatusRecordDTO];
      return column.exportType === 'number' ? (value as number) : (value as string);
    },
  }));

  return recordsToXSheet(records, exportColumns);
}
